"use client";

import React, { useState, useMemo, useCallback, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFolderOpen, faEdit } from "@fortawesome/free-solid-svg-icons";
import Button from "../modules/Button/Button";
import Table from "../modules/table/table";
import SearchBar from "../../shared-modules/SearchBar";
import Modal from "../../shared-modules/Modal";
import InfoCard from "../modules/InfoCard/InfoCard";
import Badge from "../modules/Badge/Badge";
import RadioGroup from "../modules/RadioGroup/RadioGroup";
import Input from "../modules/Input/Input";
import toast from "react-hot-toast";
import styles from "./PendingRefurbishment.module.css";
import supplyStyles from "../../../styles/inventory/OrderSupplies.module.css";
import { sendWarrantyEmail, getPendingRefurbishment, submitRefurbishment } from "../../../services/inventory/movement";
import { parseDateSafe } from "../../../utils/dateFormatters";

const SUPPLIES = [
  "AAA Batteries",
  "Tenovi Cellular Glucometer Strips & Lancets",
  "AA Batteries",
  "Tenovi Glucometer Strips & Lancets",
  "Gateway Power Supply",
  "Tenovi Glucometer Batteries",
];

const transform = (device) => ({
  id: device.id,
  serialImei: device.imei || device.serial || device.name || String(device.id),
  type: (device.device_type && (device.device_type.name || device.device_type)) || device.device_type_name || "Unknown",
  date: device.added_at ? parseDateSafe(device.added_at)?.toLocaleDateString() || "N/A" : "N/A",
  location: device.current_location_name || device.location_name || device.location || "Warehouse",
  status: device.inventory_status || "Pending Refurbishment",
  originalDevice: device,
});

const PendingRefurbishmentSection = ({ onComplete = () => {} }) => {
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [step, setStep] = useState("start");

  // answers stored as array of { label, value } where value is 'yes' or 'no'
  const [answers, setAnswers] = useState([]);
  // allow editing the answers but keep same UI format — edit allowed only after questionnaire complete
  const [editingAnswers, setEditingAnswers] = useState(false);

  const [warrantyChecking, setWarrantyChecking] = useState(false);
  const [warrantyCheckResult, setWarrantyCheckResult] = useState(null);
  const [sendingWarrantyClaim, setSendingWarrantyClaim] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [imeiSearch, setImeiSearch] = useState("");
  const [selectedSupplies, setSelectedSupplies] = useState([]);
  const [defectNotes, setDefectNotes] = useState("");
  const [supplyIdsInput, setSupplyIdsInput] = useState("");
  const [showMailModal, setShowMailModal] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [mailTo, setMailTo] = useState("");
  const [mailSubject, setMailSubject] = useState("");
  const [mailBody, setMailBody] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [apiData, setApiData] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState(null);

  const fetchList = useCallback(async (pageOrUrl = null) => {
    setLoadingList(true);
    setListError(null);
    try {
      const res = await getPendingRefurbishment(pageOrUrl);
      setApiData(res);
    } catch (err) {
      console.error("Failed to load pending refurbishment list:", err);
      setApiData({ meta: null, results: [] });
      setListError(err);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const devicesList = useMemo(() => (apiData && Array.isArray(apiData.results) ? apiData.results : []), [apiData]);
  const transformed = useMemo(() => devicesList.map(transform), [devicesList]);
  const filtered = useMemo(() => {
    const q = imeiSearch.trim().toLowerCase();
    if (!q) return transformed;
    return transformed.filter((d) => d.serialImei.toLowerCase().includes(q));
  }, [transformed, imeiSearch]);

  const pushAnswer = useCallback((label, value) => {
    if (value !== "yes" && value !== "no") return;
    setAnswers((prev) => {
      const filtered = prev.filter((x) => x.label !== label);
      return [...filtered, { label, value }];
    });
  }, []);

  const openModalFor = useCallback((device) => {
    setSelectedDevice(device);
    setStep("start");
    setAnswers([]);
    setWarrantyCheckResult(null);
    setSelectedSupplies([]);
    setDefectNotes("");
    setSupplyIdsInput("");
    setEditingAnswers(false);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedDevice(null);
    setStep("start");
    setAnswers([]);
    setWarrantyCheckResult(null);
    setProcessing(false);
    setWarrantyChecking(false);
    setSendingWarrantyClaim(false);
    setSelectedSupplies([]);
    setShowMailModal(false);
    setIsEditingEmail(false);
    setMailTo("");
    setMailSubject("");
    setMailBody("");
    setDefectNotes("");
    setSupplyIdsInput("");
    setEditingAnswers(false);
  }, []);

  const checkWarrantyApi = useCallback(async (device) => {
    setWarrantyChecking(true);
    setWarrantyCheckResult(null);
    await new Promise((r) => setTimeout(r, 600));
    const eligible = Boolean(device?.warranty_possible);
    setWarrantyChecking(false);
    setWarrantyCheckResult(eligible);
    return eligible;
  }, []);

  const isOlderThanOneYear = (dateString) => {
    if (!dateString) return false;
    const added = parseDateSafe(dateString);
    if (!added) return false;
    const now = new Date();
    const diffMs = now - added;
    const days = diffMs / (1000 * 60 * 60 * 24);
    return days >= 365;
  };

  const handleWarrantyChoice = useCallback(
    async (val) => {
      pushAnswer("Contains warranty", val);

      if (val === "yes") {
        setWarrantyChecking(true);
        await new Promise((r) => setTimeout(r, 350));
        const dateField = selectedDevice?.added_at || selectedDevice?.created_at || selectedDevice?.addedAt || selectedDevice?.createdAt;
        const olderThanYear = isOlderThanOneYear(dateField);
        setWarrantyChecking(false);

        if (!olderThanYear) {
          setWarrantyCheckResult(true);
          setStep("warranty-eligible");
          return true;
        } else {
          pushAnswer("Contains warranty", "no");
          setWarrantyCheckResult(false);
          setStep("box-question");
          toast("Device warranty time exceeded — proceeding as no warranty", { icon: "ℹ️" });
          return false;
        }
      } else {
        setStep("box-question");
        return false;
      }
    },
    [pushAnswer, selectedDevice]
  );

  const handleYesNo = useCallback((label, val, nextStep) => {
    pushAnswer(label, val);
    if (nextStep) setStep(nextStep);
  }, [pushAnswer]);

  const toggleSupply = useCallback((supply) => {
    setSelectedSupplies((prev) =>
      prev.includes(supply) ? prev.filter((item) => item !== supply) : [...prev, supply]
    );
  }, []);

  const finishSupplies = useCallback(() => {
    pushAnswer("Supplies used", selectedSupplies.length ? "yes" : "no");
    setStep("preview");
  }, [pushAnswer, selectedSupplies]);

  const questionnaireComplete = useMemo(() => step === "preview" || step === "warranty-eligible", [step]);
  const answerValue = useCallback((label) => answers.find((a) => a.label === label)?.value, [answers]);

  const parseSupplyIds = (input) => {
    if (!input) return [];
    try {
      return input
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .map((x) => Number(x))
        .filter((n) => Number.isFinite(n) && n > 0);
    } catch {
      return [];
    }
  };

  const handleSubmitRefurbishment = useCallback(async () => {
    if (!selectedDevice) return;
    const deviceIdentifier = selectedDevice.imei || selectedDevice.serial || selectedDevice.name || selectedDevice.id;

    const isUnlinkedAnswer = answerValue("Device Linked");
    const is_unlinked = isUnlinkedAnswer === "yes"; // yes => unlinked true
    const has_box = answerValue("Box included") === "yes";
    const is_defective = answerValue("Defective/Damaged") === "yes";
    const has_cuff = answerValue("Cuff included") === "yes";
    const defect_notes = defectNotes || "";

    const supplies_from_input = parseSupplyIds(supplyIdsInput);
    const supplies_used = supplies_from_input.length ? supplies_from_input : [];

    const finalPayload = {
      is_unlinked,
      has_box,
      is_defective,
      has_cuff,
      defect_notes,
      supplies_used,
    };

    setProcessing(true);
    try {
      const res = await submitRefurbishment(String(deviceIdentifier), finalPayload);
      const success =
        (res && (res.status === 200 || res.status === 201)) ||
        (res && res.data && (res.data.success === true || !!res.data.message)) ||
        res?.success === true ||
        !!res?.message;
      if (success) {
        toast.success("Refurbishment submitted successfully");
        await fetchList();
        try { onComplete(selectedDevice); } catch (e) {}
        closeModal();
      } else {
        const serverPayload = res?.data ?? res;
        const msg = serverPayload?.message || serverPayload?.error || "Failed to submit refurbishment";
        toast.error(msg);
      }
    } catch (err) {
      console.error("submitRefurbishment failed:", err);
      const serverPayload = err?.response?.data ?? err?.message ?? "Request failed";
      const msg = serverPayload?.message || serverPayload?.error || serverPayload || "Failed to submit refurbishment";
      toast.error(msg);
    } finally {
      setProcessing(false);
    }
  }, [selectedDevice, answerValue, defectNotes, supplyIdsInput, onComplete, closeModal, fetchList]);

  const handleMarkRefurbished = useCallback(() => {
    if (warrantyCheckResult === true) return;
    if (!questionnaireComplete) {
      toast.error("Please complete the questionnaire before marking refurbished.");
      return;
    }
    if (answerValue("Defective/Damaged") === "yes" && !defectNotes.trim()) {
      toast.error("Please add a short defect note describing the issue.");
      return;
    }
    const unlinkedAns = answerValue("Device Linked");
    if (unlinkedAns === "no") {
      toast.error("Device is still linked. Please unlink it first.");
      setStep("linked-block");
      return;
    }
    handleSubmitRefurbishment();
  }, [warrantyCheckResult, questionnaireComplete, answerValue, defectNotes, handleSubmitRefurbishment]);

  const openMailModalPrefilled = useCallback(() => {
    if (!selectedDevice) return;
    setIsEditingEmail(false);
    setMailTo(selectedDevice.client_email || selectedDevice.client_email_address || "");
    setMailSubject(`Warranty Notification — ${selectedDevice.imei || selectedDevice.serial || selectedDevice.id}`);
    setMailBody(
`Dear Client,

Your device (${selectedDevice.imei || selectedDevice.serial || selectedDevice.id}) appears to be eligible for warranty. Device details:
• Device ID / IMEI: ${selectedDevice.imei || selectedDevice.serial || selectedDevice.id}
• Device Type: ${selectedDevice.device_type?.name || selectedDevice.device_type || "N/A"}
• Location: ${selectedDevice.current_location_name || selectedDevice.location_name || selectedDevice.location || "N/A"}
• Notes: ${selectedDevice.notes || selectedDevice.note || "N/A"}

Please contact us to proceed with the warranty claim.

Thanks.
`
    );
    setShowMailModal(true);
  }, [selectedDevice]);

  const handleSaveEmail = useCallback(() => {
    if (!mailTo?.trim() || !mailSubject?.trim() || !mailBody?.trim()) {
      toast.error("Please complete email fields before saving");
      return;
    }
    setIsEditingEmail(false);
    toast.success("Email saved (draft)");
  }, [mailTo, mailSubject, mailBody]);

  const handleSendEmail = useCallback(async () => {
    if (!selectedDevice) return;
    if (!mailTo?.trim() || !mailSubject?.trim() || !mailBody?.trim()) {
      toast.error("Please complete email fields before sending");
      return;
    }
    if (isEditingEmail) {
      toast.error("Finish editing before sending.");
      return;
    }
    setSendingEmail(true);
    try {
      const payload = {
        device_id: selectedDevice.id,
        to_email: mailTo.trim(),
        subject: mailSubject.trim(),
        body: mailBody,
      };
      const res = await sendWarrantyEmail(payload);
      const success = res && (res.status === 200 || res.status === 201 || res.data?.success);
      if (success) {
        toast.success("Warranty email sent");
        await fetchList();
        try { onComplete(selectedDevice); } catch (e) {}
        setShowMailModal(false);
        closeModal();
      } else {
        toast.error(res?.data?.message || "Failed to send warranty email");
      }
    } catch (err) {
      console.error("sendWarrantyEmail error:", err);
      const message = err?.response?.data?.error || err?.message || "Failed to send email";
      toast.error(message);
    } finally {
      setSendingEmail(false);
    }
  }, [mailTo, mailSubject, mailBody, selectedDevice, closeModal, onComplete, isEditingEmail, fetchList]);

  const columns = [
    { key: "serialImei", label: "Serial/IMEI", alignLeft: true },
    { key: "type", label: "Device Type", alignLeft: true },
    { key: "date", label: "Date", alignLeft: true },
    { key: "location", label: "Location", alignLeft: true },
    {
      key: "status",
      label: "Status",
      alignLeft: true,
      sorting: false,
      render: (row) => <Badge dot={false}>{row.status}</Badge>,
    },
    {
      key: "actions",
      label: "Actions",
      alignLeft: true,
      sorting: false,
      render: (row) => (
        <Button
          size="small"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            openModalFor(row.originalDevice);
          }}
        >
          <FontAwesomeIcon icon={faFolderOpen} />
        </Button>
      ),
    },
  ];

  const infoCardData = selectedDevice
    ? [
        { label: "Serial/IMEI", value: selectedDevice.imei || selectedDevice.serial || "-" },
        { label: "Device Type", value: selectedDevice.device_type?.name || selectedDevice.device_type || "-" },
        { label: "Location", value: selectedDevice.current_location_name || selectedDevice.location || "-" },
        { label: "Status", value: selectedDevice.inventory_status || "-" },
        {
          label: "Added At",
          value: selectedDevice.added_at ? parseDateSafe(selectedDevice.added_at)?.toLocaleString() || "-" : "-",
        },
      ]
    : [];

  const modalFooter = (
    <div className={styles.modalFooter}>
      <Button
        size="small"
        variant="secondary"
        onClick={closeModal}
        disabled={processing || warrantyChecking || sendingWarrantyClaim || sendingEmail || showMailModal}
      >
        Cancel
      </Button>

      {warrantyCheckResult === true || step === "warranty-eligible" ? (
        <Button
          size="small"
          variant="primary"
          onClick={() => openMailModalPrefilled()}
          loading={sendingWarrantyClaim || sendingEmail}
          disabled={sendingWarrantyClaim || warrantyChecking || sendingEmail || !questionnaireComplete || showMailModal}
        >
          Send for Warranty
        </Button>
      ) : (
        <Button
          size="small"
          variant="primary"
          onClick={handleMarkRefurbished}
          loading={processing}
          disabled={
            processing ||
            warrantyChecking ||
            sendingWarrantyClaim ||
            sendingEmail ||
            !questionnaireComplete ||
            warrantyCheckResult === true ||
            showMailModal
          }
        >
          Mark as Refurbished
        </Button>
      )}
    </div>
  );

  const mailModal = (
    <Modal
      isOpen={showMailModal}
      onClose={() => { setShowMailModal(false); setIsEditingEmail(false); }}
      title={`Send Warranty Email — ${selectedDevice?.imei}`}
      width="800px"
      height="auto"
      footer={
        <div className={supplyStyles.modal_footer}>
          <div className={supplyStyles.footer_left} />
          <div className={supplyStyles.footer_right}>
            <Button variant="secondary" size="small" onClick={() => { setShowMailModal(false); setIsEditingEmail(false); }} disabled={sendingEmail}>
              Cancel
            </Button>
            {isEditingEmail ? (
              <Button variant="primary" size="small" onClick={handleSaveEmail}>
                Save
              </Button>
            ) : null}
            <Button variant="secondary" size="small" onClick={() => setIsEditingEmail((s) => !s)}>
              <FontAwesomeIcon icon={faEdit} />
            </Button>
            <Button variant="primary" size="small" onClick={handleSendEmail} loading={sendingEmail} disabled={sendingEmail || isEditingEmail}>
              {sendingEmail ? "Sending..." : "Send Email"}
            </Button>
          </div>
        </div>
      }
    >
      <div className={supplyStyles.order_supplies_content}>
        <div className={supplyStyles.step_content}>
          <div className={supplyStyles.email_container}>
            <div className={supplyStyles.email_header}>
              <h3>Email</h3>
              <Button variant="secondary" size="small" onClick={() => setIsEditingEmail((s) => !s)}>
                <FontAwesomeIcon icon={faEdit} />
              </Button>
            </div>

            <div className={supplyStyles.email_fields}>
              <div className={supplyStyles.email_field}>
                <span className={supplyStyles.email_label}>To:</span>
                {isEditingEmail ? (
                  <Input value={mailTo} onChange={(e) => setMailTo(e.target.value)} />
                ) : (
                  <span className={supplyStyles.email_value}>{mailTo || "(enter recipient email)"}</span>
                )}
              </div>

              <div className={supplyStyles.email_field}>
                <span className={supplyStyles.email_label}>Subject:</span>
                {isEditingEmail ? (
                  <Input value={mailSubject} onChange={(e) => setMailSubject(e.target.value)} />
                ) : (
                  <span className={supplyStyles.email_value}>{mailSubject}</span>
                )}
              </div>
            </div>

            <div className={supplyStyles.email_body_container}>
              {isEditingEmail ? (
                <textarea
                  className={supplyStyles.email_textarea}
                  value={mailBody}
                  onChange={(e) => setMailBody(e.target.value)}
                  rows={12}
                />
              ) : (
                <div className={supplyStyles.email_body_preview}>
                  {mailBody.split("\n").map((line, idx) => (
                    <p key={idx}>{line}</p>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );

  return (
    <div className={styles.container}>
      <SearchBar value={imeiSearch} onSearchChange={setImeiSearch} placeholder="Search IMEI" />

      <div className={styles.tableWrap}>
        <Table
          columns={columns}
          data={filtered}
          pagination
          loading={loadingList}
          meta={apiData?.meta ?? null}
          isRowClickable
          onRowClick={(row) => openModalFor(row.originalDevice)}
          onPageChange={(p) => fetchList(p)}
          paginationLoading={loadingList}
        />
      </div>

      <Modal
        title={selectedDevice ? `Refurbishment — ${selectedDevice.imei || selectedDevice.serial}` : "Refurbishment"}
        isOpen={isModalOpen}
        onClose={closeModal}
        width="840px"
        height="90vh"
        size="medium"
        closeOnOverlayClick
        closeOnEscape
        showCloseButton
        footer={modalFooter}
      >
        {!selectedDevice ? (
          <div>No device selected.</div>
        ) : (
          <div style={{ position: "relative" }}>
            <div className={styles.modalBody} aria-hidden={showMailModal}>
              <InfoCard title="Device Info" data={infoCardData} className={styles.infoCard} />

              <div className={styles.notes}>
                <strong>Notes</strong>
                <div className={styles.notesText}>{selectedDevice.notes || "No notes provided."}</div>
              </div>

              <div className={styles.questionFlow}>
                {step === "start" && (
                  <div className={styles.qContainer}>
                    <div className={styles.questionTextLarge}>Is this device unlinked?</div>

                    <RadioGroup
                      name="unlinked"
                      options={[{ label: "Yes (unlinked)", value: "yes" }, { label: "No (still linked)", value: "no" }]}
                      onChange={(v) => {
                        pushAnswer("Device Linked", v);
                        if (v === "yes") {
                          setStep("warranty-question");
                        } else {
                          setStep("linked-block");
                        }
                      }}
                      disabled={showMailModal}
                    />
                  </div>
                )}

                {step === "linked-block" && (
                  <div className={styles.qContainer}>
                    <div className={styles.finalTexterror}>Device is still linked. Please unlink it first.</div>
                  </div>
                )}

                {step === "warranty-question" && (
                  <div className={styles.qContainer}>
                    <div className={styles.questionTextLarge}>Does the device contain warranty?</div>

                    <RadioGroup
                      name="warranty"
                      options={[{ label: "Yes", value: "yes" }, { label: "No", value: "no" }]}
                      value={answers.find((a) => a.label === "Contains warranty")?.value}
                      onChange={(v) => handleWarrantyChoice(v)}
                      disabled={showMailModal}
                    />

                    {warrantyChecking && <div>Checking warranty eligibility...</div>}
                  </div>
                )}

                {step === "warranty-eligible" && (
                  <div className={styles.qContainer}>
                    <div className={styles.finalText}>Device is eligible for warranty. You must submit a claim.</div>
                  </div>
                )}

                {step === "box-question" && (
                  <div className={styles.qContainer}>
                    <div className={styles.questionTextLarge}>Did the old device come with its box?</div>

                    <RadioGroup
                      name="box"
                      options={[{ label: "Yes", value: "yes" }, { label: "No", value: "no" }]}
                      onChange={(v) => handleYesNo("Box included", v, "defect-question")}
                      disabled={showMailModal}
                    />
                  </div>
                )}

                {step === "defect-question" && (
                  <div className={styles.qContainer}>
                    <div className={styles.questionTextLarge}>Is the device defective or damaged?</div>

                    <RadioGroup
                      name="defect"
                      options={[{ label: "Yes", value: "yes" }, { label: "No", value: "no" }]}
                      onChange={(v) => {
                        pushAnswer("Defective/Damaged", v);
                        if (v === "yes") {
                          setStep("defect-notes");
                        } else {
                          setStep("cuff-question");
                        }
                      }}
                      disabled={showMailModal}
                    />
                  </div>
                )}

                {step === "defect-notes" && (
                  <div className={styles.qContainer}>
                    <div className={styles.questionTextLarge}>Please add a short defect note</div>
                    <div style={{ marginTop: 8 }}>
                      <Input
                        type="textarea"
                        value={defectNotes}
                        onChange={(e) => setDefectNotes(e.target.value)}
                        placeholder="Short description of the defect (e.g. minor scratches on cuff)"
                        rows={4}
                        disabled={showMailModal}
                      />
                    </div>
                    <div style={{ marginTop: 12 }}>
                      <Button size="small" variant="primary" onClick={() => setStep("cuff-question")} disabled={showMailModal}>
                        Continue
                      </Button>
                      <Button size="small" variant="secondary" style={{ marginLeft: 8 }} onClick={() => { setDefectNotes(""); setStep("cuff-question"); }} disabled={showMailModal}>
                        Skip
                      </Button>
                    </div>
                  </div>
                )}

                {step === "cuff-question" && (
                  <div className={styles.qContainer}>
                    <div className={styles.questionTextLarge}>Does the device come with a cuff?</div>

                    <RadioGroup
                      name="cuff"
                      options={[{ label: "Yes", value: "yes" }, { label: "No", value: "no" }]}
                      onChange={(v) => handleYesNo("Cuff included", v, "supplies-question")}
                      disabled={showMailModal}
                    />
                  </div>
                )}

                {step === "supplies-question" && (
                  <div className={styles.qContainer}>
                    <div className={styles.questionTextLarge}>Did you use any supplies?</div>

                    <RadioGroup
                      name="suppliesUsed"
                      options={[{ label: "Yes", value: "yes" }, { label: "No", value: "no" }]}
                      onChange={(v) => (v === "yes" ? setStep("supplies-selector") : (pushAnswer("Supplies used", "no"), setStep("preview")))}
                      disabled={showMailModal}
                    />
                  </div>
                )}

                {step === "supplies-selector" && (
                  <div className={styles.qContainer}>
                    <div className={styles.questionTextLarge}>Select supplies used</div>

                    {SUPPLIES.map((s) => (
                      <label key={s} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <input type="checkbox" checked={selectedSupplies.includes(s)} onChange={() => toggleSupply(s)} disabled={showMailModal} />
                        {s}
                      </label>
                    ))}

                    <div style={{ marginTop: 12 }}>
                      <div style={{ color: "#6b7280", fontSize: 13, marginBottom: 8 }}>
                        Optionally enter numeric supply IDs (comma separated) to include in payload (e.g. 24,25)
                      </div>
                      <Input value={supplyIdsInput} onChange={(e) => setSupplyIdsInput(e.target.value)} placeholder="Enter supply IDs (e.g. 24,25)" />

                      <div style={{ marginTop: 12 }}>
                        <Button size="small" variant="primary" onClick={finishSupplies} disabled={showMailModal}>
                          Done
                        </Button>

                        <Button size="small" variant="secondary" style={{ marginLeft: 8 }} onClick={() => { setSelectedSupplies([]); setSupplyIdsInput(""); pushAnswer("Supplies used", "no"); setStep("preview"); }} disabled={showMailModal}>
                          Skip
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Preview + editing area (keeps original layout) */}
                <div className={styles.livePreview} style={{ marginTop: 16, borderTop: "1px solid #eee", paddingTop: 12 }}>
                  {answers.length > 0 && (
                    <div className={styles.answersWrap}>
                      {answers.map((a, i) => (
                        <div key={`${a.label}-${i}`} style={{ display: "flex", justifyContent: "space-between", padding: 8, borderBottom: "1px solid #f5f5f5" }}>
                          <div>{a.label}</div>
                          <div><strong>{a.value}</strong></div>
                        </div>
                      ))}

                      {defectNotes && (
                        <div style={{ display: "flex", justifyContent: "space-between", padding: 8, borderBottom: "1px solid #f5f5f5" }}>
                          <div>Defect note</div>
                          <div><strong>{defectNotes}</strong></div>
                        </div>
                      )}

                      {selectedSupplies.length > 0 && (
                        <div style={{ display: "flex", justifyContent: "space-between", padding: 8, borderBottom: "1px solid #f5f5f5" }}>
                          <div>Supplies selected</div>
                          <div><strong>{selectedSupplies.join(", ")}</strong></div>
                        </div>
                      )}

                      {supplyIdsInput && (
                        <div style={{ display: "flex", justifyContent: "space-between", padding: 8, borderBottom: "1px solid #f5f5f5" }}>
                          <div>Supply IDs</div>
                          <div><strong>{supplyIdsInput}</strong></div>
                        </div>
                      )}

                      {/* Edit answers inline (YES/NO) - only shown after questionnaireComplete and toggled via button */}
                      {questionnaireComplete && (
                        <div style={{ marginTop: 12 }}>
                          <Button size="small" variant="secondary" onClick={() => setEditingAnswers((s) => !s)}>{editingAnswers ? "Stop Editing" : "Edit Answers"}</Button>
                        </div>
                      )}

                      {editingAnswers && (
                        <div style={{ marginTop: 12 }}>
                          {["Device Linked", "Contains warranty", "Box included", "Defective/Damaged", "Cuff included", "Supplies used"].map((q) => (
                            <div key={q} style={{ marginBottom: 10 }}>
                              <div style={{ fontSize: 14, marginBottom: 6 }}>{q}</div>
                              <RadioGroup
                                name={`edit-${q}`}
                                options={[{ label: "Yes", value: "yes" }, { label: "No", value: "no" }]}
                                value={answers.find((a) => a.label === q)?.value}
                                onChange={(v) => pushAnswer(q, v)}
                              />
                            </div>
                          ))}

                          <div style={{ marginTop: 8 }}>
                            <div style={{ marginBottom: 6 }}>Defect note (editable)</div>
                            <Input type="textarea" value={defectNotes} onChange={(e) => setDefectNotes(e.target.value)} rows={4} />
                          </div>

                          <div style={{ marginTop: 8 }}>
                            <div style={{ marginBottom: 6 }}>Supply IDs (numeric, comma separated) — used for payload</div>
                            <Input value={supplyIdsInput} onChange={(e) => setSupplyIdsInput(e.target.value)} placeholder="e.g. 24,25" />
                          </div>

                          <div style={{ marginTop: 12 }}>
                            <Button size="small" variant="primary" onClick={() => setEditingAnswers(false)}>Save Edits</Button>
                          </div>
                        </div>
                      )}

                    </div>
                  )}
                </div>

              </div>

              {showMailModal && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: "rgba(255,255,255,0.75)",
                    zIndex: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pointerEvents: "auto",
                  }}
                >
                  <div style={{ textAlign: "center", color: "#374151" }}>
                    <div style={{ fontSize: 16, marginBottom: 8 }}>Warranty email is open</div>
                    <div style={{ fontSize: 13, color: "#6b7280" }}>Finish or close the email to continue refurbishment actions.</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      {mailModal}
    </div>
  );
};

export default PendingRefurbishmentSection;